'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, X, Check, CheckCheck, AlertTriangle, Info, Zap, Mail, Clock } from 'lucide-react';
import {
  CognitiveAlertEvent, AlertSeverity,
  getAlertEvents, markAlertRead, markAllAlertsRead
} from '@/lib/services/alertThresholdService';
import { useAuth } from '@/contexts/AuthContext';

const severityIcon: Record<AlertSeverity, React.ReactNode> = {
  info: <Info size={11} className="text-sky-400" />,
  warning: <AlertTriangle size={11} className="text-amber-400" />,
  critical: <Zap size={11} className="text-red-400" />,
};

const severityBorder: Record<AlertSeverity, string> = {
  info: 'border-sky-800/40',
  warning: 'border-amber-800/40',
  critical: 'border-red-800/40',
};

const severityBg: Record<AlertSeverity, string> = {
  info: 'bg-sky-950/20',
  warning: 'bg-amber-950/20',
  critical: 'bg-red-950/20',
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

interface CognitiveAlertBellProps {
  /** Poll interval in ms (default 30 000) */
  pollInterval?: number;
}

export default function CognitiveAlertBell({ pollInterval = 30_000 }: CognitiveAlertBellProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<CognitiveAlertEvent[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const unread = events.filter(e => !e.is_read).length;
  const hasCritical = events.some(e => !e.is_read && e.severity === 'critical');

  const fetchEvents = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getAlertEvents(user.id, { limit: 30 });
      setEvents(data);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, pollInterval);
    return () => clearInterval(interval);
  }, [fetchEvents, pollInterval]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleMarkRead = async (id: string) => {
    await markAlertRead(id);
    setEvents(prev => prev.map(e => e.id === id ? { ...e, is_read: true } : e));
  };

  const handleMarkAllRead = async () => {
    if (!user) return;
    setLoading(true);
    await markAllAlertsRead(user.id);
    setEvents(prev => prev.map(e => ({ ...e, is_read: true })));
    setLoading(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(v => !v)}
        className="relative w-9 h-9 flex items-center justify-center rounded-xl transition-all hover:bg-white/5"
        style={{ border: open ? '1px solid rgba(249,115,22,0.4)' : '1px solid transparent' }}
        aria-label="Cognitive alerts"
      >
        <Bell size={16} style={{ color: hasCritical ? '#ef4444' : unread > 0 ? '#F97316' : '#4A7AB5' }} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full text-[9px] font-bold px-1"
            style={{
              background: hasCritical ? '#ef4444' : '#F97316',
              color: '#fff',
              animation: hasCritical ? 'pulse 1.5s ease-in-out infinite' : undefined,
            }}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-2xl overflow-hidden z-50 shadow-2xl"
          style={{ background: '#0B1628', border: '1px solid #1E3A5F', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1E3A5F]/60">
            <div className="flex items-center gap-2">
              <Bell size={13} style={{ color: '#F97316' }} />
              <span className="text-sm font-bold text-white">Cognitive Alerts</span>
              {unread > 0 && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-[#F97316]/15 text-[#F97316] border border-[#F97316]/30">
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  disabled={loading}
                  className="flex items-center gap-1 text-[10px] text-zinc-400 hover:text-emerald-400 transition-colors px-2 py-1 rounded-lg hover:bg-emerald-950/20"
                >
                  <CheckCheck size={11} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors">
                <X size={12} style={{ color: '#4A7AB5' }} />
              </button>
            </div>
          </div>

          {/* Events List */}
          <div className="max-h-[400px] overflow-y-auto">
            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <Bell size={24} className="text-zinc-700" />
                <p className="text-xs text-zinc-600">No alerts yet</p>
                <p className="text-[10px] text-zinc-700">Alerts fire when metrics cross configured thresholds</p>
              </div>
            ) : (
              <div className="divide-y divide-[#1E3A5F]/30">
                {events.map(ev => (
                  <div
                    key={ev.id}
                    className={`px-4 py-3 transition-all ${!ev.is_read ? severityBg[ev.severity] : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border ${severityBorder[ev.severity]}`}
                        style={{ background: ev.severity === 'critical' ? 'rgba(239,68,68,0.1)' : ev.severity === 'warning' ? 'rgba(245,158,11,0.1)' : 'rgba(14,165,233,0.1)' }}>
                        {severityIcon[ev.severity]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                          <span className={`text-[9px] font-bold uppercase tracking-widest ${ev.severity === 'critical' ? 'text-red-400' : ev.severity === 'warning' ? 'text-amber-400' : 'text-sky-400'}`}>
                            {ev.severity}
                          </span>
                          {ev.email_sent && (
                            <span className="flex items-center gap-0.5 text-[9px] text-violet-400">
                              <Mail size={8} /> emailed
                            </span>
                          )}
                          {!ev.is_read && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-white leading-snug">{ev.message}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="flex items-center gap-1 text-[10px] text-zinc-500">
                            <Clock size={9} /> {timeAgo(ev.triggered_at)}
                          </span>
                          {!ev.is_read && (
                            <button
                              onClick={() => handleMarkRead(ev.id)}
                              className="flex items-center gap-0.5 text-[10px] text-zinc-500 hover:text-emerald-400 transition-colors"
                            >
                              <Check size={9} /> Mark read
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {events.length > 0 && (
            <div className="px-4 py-2 border-t border-[#1E3A5F]/40 text-center">
              <p className="text-[10px] text-zinc-600">
                Showing last {events.length} alerts · Thresholds configurable per patient
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
